import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import EditIcon from '@mui/icons-material/DriveFileRenameOutline';
import OwnerIcon from '@mui/icons-material/AccountBox';
import { useHistory } from 'react-router-dom';
import PlayIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import StorageIcon from '@mui/icons-material/Storage';
import { isNumber } from 'lodash';
import CheckIcon from '@mui/icons-material/Check';

import { saveCurrentPlayingTimelineClip, updateNotes, updateQualifiers } from '../../store/videos/videoSlice';
import { switchSidebar } from '../../store/sharedStore/sharedStoreSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { PaginationFooter } from '../modules/PaginationFooter';
import { STATUS } from '../../enums/ENUMS';
import { SortOptions, SortType } from '../../enums/SORT';
import LinearProgress from '../modules/LinearProgress';
import SearchInput from '../modules/SearchInput';
import styles from './AllContent.module.scss';
import {
  buildQueryString,
  getAllContentPagePaginationLimit,
  getAllContentSortOptionAndType,
  getSortStateForAllContent,
  parseQueryString,
  setAllContentPagePaginationLimit,
  setAllContentSortOptionAndType,
  useDimensions,
} from '../../helpers';
import { FOLDER_TYPE, PATHS, ROLES } from '../../enums';
import { getAllContentByOrgId } from '../../api';
import AllContentComposer from './AllContentComposer';
import AllContentFolder from './AllContentFolder/AllContentFolder';
import useDebounce from '../../helpers/useDebounce';
import { ContainedButton, OutlinedButton } from '../modules/Buttons';
import { FilterIcon } from '../../assets/icons/filterIcon';
import { AllContentFiltersModal } from './AllContentFilters';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../enums/ACCESS_ENUM';
import { Tooltip } from '@mui/material';
import ShareFolderModal from '../SharingModals/MyFolderPage/ShareFolderModal';
import VideofeedShareModal from '../SharingModals/MyFolderPage/VideofeedShareModal';
import DateSortHeader from '../modules/DateSortHeader';

const AllContent = () => {
  const dispatch = useAppDispatch();
  const { role, myOrganization, currentUser } = useAppSelector((state) => state.user);
  const history = useHistory();
  const { widthD } = useDimensions();
  const [allContent, setAllContent] = useState<FOLDER_TYPE[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState(SortType.ASC);
  const [sortField, setSortField] = useState(SortOptions.NAME);
  const [loading, setLoading] = useState(false);
  const [filterBy, setFilterBy] = useState<ACCESS_TYPE_ENUM[] | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<{ label: string; value: string }[]>([{ label: 'All', value: 'ALL' }]);

  useEffect(() => {
    if (role && role.toLowerCase() === ROLES.VIEWER.toLowerCase()) {
      history.replace(PATHS.SHARED_WITH_ME.path);
    }
  }, [role]);
  useEffect(() => {
    dispatch(switchSidebar(false));
    dispatch(updateNotes([]));
    dispatch(updateQualifiers([]));
    dispatch(saveCurrentPlayingTimelineClip(null));
    const prefferedLimit = getAllContentPagePaginationLimit();
    if (prefferedLimit && isNumber(+prefferedLimit)) {
      setPage(0);
      setLimit(parseInt(prefferedLimit, 10));
    }
  }, []);

  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce<string>(search, 500);
  const type = localStorage.getItem('allContentType')
    ? (localStorage.getItem('allContentType') as 'folder' | 'composer' | 'all' | null)
    : 'all';

  const fetchAllFolders = async (sortFromArg?: SortType, sortOpinionFromArg?: SortOptions) => {
    if (!myOrganization?.id) return;
    setLoading(true);
    try {
      let usersQuery = selectedUsers[0].value === 'ALL' ? null : selectedUsers.map((u) => u.value);
      const activeSort = sortFromArg || sort;
      const activeSortField = sortOpinionFromArg || sortField;
      const res = await getAllContentByOrgId(
        +myOrganization.id,
        page,
        limit,
        activeSort,
        activeSortField,
        search,
        filterBy,
        JSON.stringify(type),
        usersQuery,
      );
      if (res?.data?.documents) {
        const arr = [...res.data.documents];
        if (activeSortField === SortOptions.LAST_VIEWED && activeSort === SortType.ASC) {
          arr.sort((a, b) => {
            return new Date(b.maxrecentview).getTime() - new Date(a.maxrecentview).getTime();
          });
        } else if (activeSortField === SortOptions.LAST_VIEWED && activeSort === SortType.DESC) {
          arr.sort((a, b) => {
            return new Date(a.maxrecentview).getTime() - new Date(b.maxrecentview).getTime();
          });
        }
        setAllContent(arr);
        setTotal(res.data.total);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const [fetchTrigger, setFetchTrigger] = useState(0);
  useEffect(() => {
    const res = getAllContentSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      if (sortAndOption[0] && sortAndOption[1]) {
        setSort(sortAndOption[0] as SortType);
        setSortField(sortAndOption[1] as SortOptions);
        fetchAllFolders(sortAndOption[0] as SortType, sortAndOption[1] as SortOptions);
      }
    } else {
      fetchAllFolders();
    }
  }, [debouncedValue, page, limit, sort, sortField, fetchTrigger, myOrganization?.id, filterBy, type, selectedUsers]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearch(e.target.value);
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAllContentPagePaginationLimit(parseInt(e.target.value, 10));
    setPage(0);
    setLimit(parseInt(e.target.value, 10));
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const sortHandler = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    if (loading) return;
    if (option) {
      setSort(option.order);
      setSortField(option.value as SortOptions);
      // @ts-ignore
      setAllContentSortOptionAndType(option.order, option.value);
    } else if (sortField === singleValue) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      setSort(sortT);
      setAllContentSortOptionAndType(sortT, singleValue);
    } else {
      const sortState = JSON.parse(getSortStateForAllContent()!);
      let sortType = SortType.ASC;
      if (sortState && sortState[singleValue]) {
        sortType = sortState[singleValue];
      }
      setSort(sortType);
      setSortField(singleValue); // <-- Add this line
      setAllContentSortOptionAndType(sortType, singleValue);
    }
    setPage(0);
  };

  const fetchDataCallback = () => {
    setFetchTrigger(Date.now());
  };

  useEffect(() => {
    if (listRef?.current) {
      listRef.current.scrollTop = 0;
    }
  }, [sort, sortField, page, limit, debouncedValue, filterBy]);

  const onNameSort = (order?: SortType) => {
    if (loading) return;
    let sortT = sortField === SortOptions.NAME && sort === SortType.ASC ? SortType.DESC : SortType.ASC;

    if (order) {
      sortT = order;
    }
    setSort(sortT);
    setAllContentSortOptionAndType(sortT, SortOptions.NAME);
    setSortField(SortOptions.NAME);
    onPaginate(null, 0);
  };

  const onSizeSort = () => {
    if (loading) return;
    let sortT = sortField === SortOptions.SIZE && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setAllContentSortOptionAndType(sortT, SortOptions.SIZE);
    setSortField(SortOptions.SIZE);
    onPaginate(null, 0);
  };

  const onOwnerSort = () => {
    if (loading) return;
    let sortT = sortField === SortOptions.OWNER && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setAllContentSortOptionAndType(sortT, SortOptions.OWNER);
    setSortField(SortOptions.OWNER);
    onPaginate(null, 0);
  };

  const onLastViewedSort = () => {
    if (loading) return;
    const sortT = sortField === SortOptions.LAST_VIEWED && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setAllContentSortOptionAndType(sortT, SortOptions.LAST_VIEWED);
    setSortField(SortOptions.LAST_VIEWED);
    onPaginate(null, 0);
  };

  const onLastEditSort = () => {
    if (loading) return;
    const sortT = sortField === SortOptions.UPDATED_AT && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    setSort(sortT);
    setAllContentSortOptionAndType(sortT, SortOptions.UPDATED_AT);
    setSortField(SortOptions.UPDATED_AT);
    onPaginate(null, 0);
  };

  const sortByPermission = (permissionArr: ACCESS_TYPE_ENUM[] | null) => {
    if (permissionArr && permissionArr.length === 0) {
      setFilterBy(null);
    } else {
      setFilterBy(permissionArr);
    }
    onPaginate(null, 0);
  };

  const setTypeFromFilter = (t: any) => {
    if (t === 'folder') {
      const urlObject = parseQueryString(window.location.search);
      if (urlObject?.prevPageWasSharedComposer) {
        delete urlObject?.prevPageWasSharedComposer;
      }
      window.history.replaceState(null, '', `?${buildQueryString(urlObject)}`);
    }
    localStorage.setItem('allContentType', t);
    onPaginate(null, 0);
  };

  const [filtersModalOpen, setFiltersModal] = useState(false);

  const switchFiltersModal = () => {
    if (loading) return;
    setFiltersModal(!filtersModalOpen);
  };

  const listRef = useRef<HTMLDivElement>(null);
  return (
    <div className={styles.myFolders}>
      <div className={styles.searchWrapper}>
        <div className={styles.searchContainer}>
          <SearchInput quickFilter onChange={onSearch} className={styles.search} />
          <div className={styles.sortToolbar}>
            {sortField === SortOptions.NAME && (
              <OutlinedButton
                kind="purple"
                className={styles.optionBtn}
                onClick={() => {
                  if (!loading) onNameSort(sort === SortType.ASC ? SortType.DESC : SortType.ASC);
                }}
              >
                <div className={styles.leftSide}>
                  {sort === SortType.ASC ? (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                      Name (A-Z)
                    </>
                  ) : (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                      Name (Z-A)
                    </>
                  )}
                </div>
                <CheckIcon sx={{ height: '16px' }} />
              </OutlinedButton>
            )}

            {sortField === SortOptions.LAST_VIEWED && (
              <OutlinedButton onClick={onLastViewedSort} kind={'purple'} className={styles.optionBtn}>
                {' '}
                <div className={styles.leftSide}>
                  <PlayIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                  Last Viewed
                </div>
                {sortField === SortOptions.LAST_VIEWED && sort === SortType.ASC && (
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
                {sortField === SortOptions.LAST_VIEWED && sort === SortType.DESC && (
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
              </OutlinedButton>
            )}
            {sortField === SortOptions.UPDATED_AT && (
              <OutlinedButton onClick={onLastEditSort} kind={'purple'} className={styles.optionBtn}>
                {' '}
                <div className={styles.leftSide}>
                  <EditIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                  Last Modified
                </div>
                {sortField === SortOptions.UPDATED_AT && sort === SortType.DESC && (
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
                {sortField === SortOptions.UPDATED_AT && sort === SortType.ASC && (
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
              </OutlinedButton>
            )}

            {sortField === SortOptions.CREATED_AT && (
              <OutlinedButton onClick={() => sortHandler(SortOptions.CREATED_AT)} kind={'purple'} className={styles.optionBtn}>
                {' '}
                <div className={styles.leftSide}>
                  <EditIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                  Created At
                </div>
                {sortField === SortOptions.CREATED_AT && sort === SortType.DESC && (
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
                {sortField === SortOptions.CREATED_AT && sort === SortType.ASC && (
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
              </OutlinedButton>
            )}

            {sortField === SortOptions.SIZE && (
              <OutlinedButton onClick={onSizeSort} kind={'purple'} className={styles.optionBtn}>
                {' '}
                <div className={styles.leftSide}>
                  <StorageIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                  Size
                </div>
                {sortField === SortOptions.SIZE && sort === SortType.DESC && (
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
                {sortField === SortOptions.SIZE && sort === SortType.ASC && (
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
              </OutlinedButton>
            )}

            {sortField === SortOptions.OWNER && (
              <OutlinedButton onClick={onOwnerSort} kind={'purple'} className={styles.optionBtn}>
                {' '}
                <div className={styles.leftSide}>
                  <OwnerIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                  Owner
                </div>
                {sortField === SortOptions.OWNER && sort === SortType.ASC && (
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
                {sortField === SortOptions.OWNER && sort === SortType.DESC && (
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
              </OutlinedButton>
            )}

            {sortField === SortOptions.ORIGINS && (
              <OutlinedButton onClick={() => sortHandler(SortOptions.ORIGINS)} kind={'purple'} className={styles.optionBtn}>
                {' '}
                <div className={styles.leftSide}>
                  <OwnerIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                  Origins
                </div>
                {sortField === SortOptions.ORIGINS && sort === SortType.ASC && (
                  <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
                {sortField === SortOptions.ORIGINS && sort === SortType.DESC && (
                  <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '12px' }} />
                )}
              </OutlinedButton>
            )}

            {selectedUsers?.length > 0 && selectedUsers[0].value !== 'ALL' && (
              <Tooltip
                arrow
                title={
                  <div className={styles.ownerTooltip}>
                    {selectedUsers.map((u) => (
                      <div className={styles.tooltipLi}>{u.label}</div>
                    ))}
                  </div>
                }
              >
                <div>
                  <OutlinedButton kind={'purple'} className={styles.optionBtn}>
                    <div className={styles.leftSide}>
                      <OwnerIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                      {selectedUsers.length} {selectedUsers.length > 1 ? 'Owners' : selectedUsers[0].label}
                    </div>
                  </OutlinedButton>
                </div>
              </Tooltip>
            )}

            {filterBy && (
              <Tooltip
                arrow
                title={
                  <div className={styles.ownerTooltip}>
                    {filterBy.map((u) => (
                      <div className={styles.tooltipLi}>{ACCESS_TYPE[u as ACCESS_TYPE_ENUM]}</div>
                    ))}
                  </div>
                }
              >
                <div>
                  <OutlinedButton kind={'purple'} className={styles.optionBtn}>
                    <div className={styles.leftSide}>
                      <OwnerIcon style={{ fontSize: '15px', marginLeft: '4px', marginRight: '8px' }} />
                      {filterBy.length} Permission(s)
                    </div>
                  </OutlinedButton>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <ContainedButton className={styles.filterButton} onClick={switchFiltersModal}>
          <FilterIcon style={{ marginRight: '12px' }} /> Sort & Filter
        </ContainedButton>
      </div>
      {loading && (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      )}
      {allContent.length > 0 && (
        <>
          <div className={styles.toolbar}>
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <div className={styles.name} onClick={() => sortHandler(SortOptions.NAME)}>
                  Name
                  {sortField === SortOptions.NAME && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
                    </>
                  )}
                  {sortField === SortOptions.NAME && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '15px', marginLeft: '4px' }} />
                    </>
                  )}
                </div>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.team} onClick={() => sortHandler(SortOptions.OWNER)}>
                  Owner
                  {sortField === SortOptions.OWNER && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />
                    </>
                  )}
                  {sortField === SortOptions.OWNER && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />
                    </>
                  )}
                </div>
                <div className={styles.permissions}>Permissions</div>
                <div className={styles.team} onClick={() => sortHandler(SortOptions.ORIGINS)}>
                  Origins
                  {sortField === SortOptions.ORIGINS && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />
                    </>
                  )}
                  {sortField === SortOptions.ORIGINS && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />
                    </>
                  )}
                </div>
                <div className={styles.date}>
                  <DateSortHeader
                    sortOption={sortField}
                    sort={sort}
                    compact={widthD <= 1330}
                    onSelect={sortHandler}
                    arrowClassName={styles.arrow}
                  />
                </div>
                <div className={styles.size} onClick={() => sortHandler(SortOptions.SIZE)}>
                  {widthD > 1330 ? 'File Size' : 'Size'}
                  {sortField === SortOptions.SIZE && sort === SortType.DESC && (
                    <>
                      <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />
                    </>
                  )}
                  {sortField === SortOptions.SIZE && sort === SortType.ASC && (
                    <>
                      <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />
                    </>
                  )}
                </div>

                <div className={styles.duration}> {widthD > 1330 ? 'Upload Hours' : 'Hours'}</div>

                <div className={styles.actionBtns}>Actions</div>
              </div>
            </div>
          </div>
          <div ref={listRef} className={styles.foldersList} id={'folder_custom_scroller'}>
            {allContent.map((content: any) => {
              if (content.type === 'folder') {
                return (
                  <div key={content.id} className="mb-5">
                    <AllContentFolder folder={content} fetchDataCallback={fetchDataCallback} dateSortOption={sortField} />
                  </div>
                );
              } else if (content.type === 'MY_COMPOSER') {
                return (
                  <div key={content.id} className="mb-5">
                    <AllContentComposer
                      fetchDataCallback={fetchDataCallback}
                      composer={content}
                      sharedComposer={content?.created_by?.id === currentUser?.id}
                      dateSortOption={sortField}
                    />
                  </div>
                );
              }
            })}
          </div>
          <PaginationFooter
            hideVersion
            page={page}
            total={total}
            onPageChange={onPaginate}
            rowsPerPage={limit}
            onRowsPerPageChange={onRowsChange}
          />
        </>
      )}
      {allContent.length === 0 && status === STATUS.IDLE && (
        <div className={styles.noFoldersContainer}>
          <div className={styles.noFoldersText}>No Content</div>
          <div className="emptyStateHint">Add your first folder from the left sidebar!</div>
        </div>
      )}
      <AllContentFiltersModal
        onNameSort={onNameSort}
        onLastViewedSort={onLastViewedSort}
        onLastEditSort={onLastEditSort}
        onSizeSort={onSizeSort}
        sortByPermission={sortByPermission}
        setType={setTypeFromFilter}
        open={filtersModalOpen}
        switchModal={switchFiltersModal}
        sortOption={sortField}
        sortOrder={sort}
        loading={loading}
        type={type}
        disableSortBySize={type === 'composer'}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
      />
      <ShareFolderModal defaultShareAccess={ACCESS_TYPE.COMMENT_AND_CREATE} setFetchTrigger={setFetchTrigger} allowCurrentUserShare />
      <VideofeedShareModal defaultShareAccess={ACCESS_TYPE.COMMENT_AND_CREATE} allowCurrentUserShare />
    </div>
  );
};

export default AllContent;
